import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Button, Card } from 'react-bootstrap';
import css from './SupplierCard.module.scss';
import cl from 'classnames';
import ResponsiveImage from '@ui/Image';
import { FlyingMailIcon, PeopleIcon } from '@assets/icons';
import { faCircleDollar } from '@fortawesome/pro-light-svg-icons';
import Show from '@ui/Show';
import RatingAndAddress from '../Elements/RatingAndAddress';
import { getCDNImageUrl } from '@helper/image';
import { queryClient } from '@contexts/QueryClientContext';
import { openEnquiryFormModal } from '@components/EnquiryForm/service';
import { dashboardTrackingEnabled, ga4Event } from '@helper/gtm';
import { useRegionsStatesMap } from '@queries/useRegions';

export const BadgeBase = ({ children, color }: { children: React.ReactNode, color: string }) => {
    return (<div className={cl(css.cardBadge, css[`cardBadge--${color}`])}>
        {children}
    </div>)
}

const Badge = ({ type }: { type: 'exclusive' | 'contacted' }) => {
    switch (type) {
        case 'exclusive': return <BadgeBase color="premium">Exclusive offers</BadgeBase>
        case 'contacted': return <BadgeBase color="white"><FlyingMailIcon className={css.contactedIcon}/> Contacted</BadgeBase>
        default: return null
    }
}

interface SupplierPreviewProps {
    name: string;
    rating: number;
    reviews: number;
    region: string;
    url?: string;
    guests?: string;
    priceTier?: 1 | 2 | 3 | 4;
}

const PRICE_TIERS = {
    1: 'Modest',
    2: 'Classic',
    3: 'Premium',
    4: 'Luxury',
}

export function SupplierPreview({ name, rating, reviews, guests, priceTier, region, url = ''}: SupplierPreviewProps) {
    return (<div className={css.preview}>
        <RatingAndAddress rating={rating} reviews={reviews} location1={region} />
        <div className={css.title}>
            {!!url ? <a href={url} target='_blank'>{name}</a> : name}
        </div>
        <div className={cl('d-flex gap-3', css.properties)}>
            {guests && <div className={css.guests}><PeopleIcon/> {guests}</div>}
            <Show when={!!priceTier}>
                <div className={css.price}><Icon icon={faCircleDollar} /> {'$'.repeat(Number(priceTier))} {PRICE_TIERS[priceTier!]}</div>
            </Show>
        </div>
    </div>)
}

type VWPDashboardSupplierProps = {
    badge?: 'exclusive' | 'contacted',
    action: React.ReactNode
    cardSize?: SizeName,
    customSize?: CardSize,
    width?: number;
    height?: number;
    supplier: Supplier,
    categoryName?: string;
    showActionButtons?: boolean;
    children?: React.ReactNode;
    onCardClick?: () => void;
}

export type Supplier = {
    name: string;
    rating: number;
    reviews: number;
    thumbnail: string;
    region: string;
    url?: string;
    guests?: string;
    priceTier?: 1 | 2 | 3 | 4;
    supplierId?: number;
    categoryId?: number;
    categorySlug?: string;
    supplierRegionId?: number;
}

type CardSize = {
    desktop: { width: number, height: number },
    mobile?: { width: number, height: number },
}

type SizeName = 'dashboard' | 'dashboardSaved' | 'small';

const SIZES: Record<SizeName, CardSize> = {
    dashboard: { desktop: { width: 100, height: 356 }, mobile: { width: 305, height: 330 } },
    dashboardSaved: { desktop: { width: 100, height: 458 }, mobile: { width: 305, height: 401 } },
    small: { desktop: { width: 19, height: 259 }, mobile: { width: 100, height: 300 } },
}

const useSizeStyle = (size: SizeName, customSize?: CardSize) => {
    const { desktop, mobile } = customSize || SIZES[size] || SIZES['dashboard'];

    return {
        '--card-width': `${desktop.width}%`,
        '--card-height': `${desktop.height}px`,
        '--card-mobile-width': `${mobile?.width ?? desktop.width}%`,
        '--card-mobile-height': `${mobile?.height ?? desktop.height}px`,
    };
}

const VWPDashboardSupplier = ({
    badge,
    action,
    supplier,
    categoryName,
    cardSize = 'dashboard',
    customSize,
    showActionButtons,
    children,
    onCardClick
}: VWPDashboardSupplierProps) => {
    const { regionsMap, statesMap } = useRegionsStatesMap();
    const style = useSizeStyle(cardSize, customSize);
    const [enquirySent, setEnquirySent] = React.useState(false);
    const isNotCustomSupplier = !!supplier.supplierId && !!supplier.categoryId && !!supplier.categorySlug && !!supplier.supplierRegionId;

    const handleSupplierAction = () => {
        if (badge === 'contacted') {
            window.open(supplier.url, '_blank');
        }
        else {
            if(!!supplier.supplierId && !!supplier.categoryId && !!supplier.categorySlug && !!supplier.supplierRegionId) {
                const region = regionsMap?.[supplier.supplierRegionId];
                openEnquiryFormModal({
                    supplier: {
                        id: supplier.supplierId,
                        name: supplier.name,
                        regionId: supplier.supplierRegionId,
                        region: region?.name,
                        state: !!region ? statesMap?.[region.stateId]?.name : undefined,
                        category: categoryName,
                    },
                    category: {
                        id: supplier.categoryId,
                        slug: supplier.categorySlug,
                    },
                    source: 'Dashboard',
                    onCompleted: () => {
                        setEnquirySent(true);
                        queryClient.invalidateQueries({ queryKey: ['all-contacted-and-saved'] });
                        queryClient.invalidateQueries({ queryKey: ['all-categories'] });
                        ga4Event('enquiry_sent_dashboard', supplier, dashboardTrackingEnabled());
                    },
                });
            }
        }
    };
    // As enquiry is not sent instantly in api call, we need to indicate to user on ui that enquiry is sent
    const buttonText = enquirySent ? 'Enquiry Sent' : 
        (badge === 'contacted' ? 'View Supplier' : <><FlyingMailIcon className={css.contactedIcon} /> Enquire Now</>);
    
    return (
        <Card className={css.container} style={style as React.CSSProperties}>
            <div className={css.imageOverlay}>
                <Show when={!!badge} otherwise={<div></div>}><Badge type={badge ?? "exclusive"} /></Show>
                {action}
            </div>
            <ResponsiveImage
                src={getCDNImageUrl(supplier.thumbnail, {width: 383, height: 220})}
                className={`${css.cardImage} card-img-top`}
                width="100%"
                style={{ objectFit: "cover" }}
                onClick={onCardClick}
            />
            <Card.Body className={css.cardBody}>
                <SupplierPreview
                    name={supplier.name}
                    url={supplier.url}
                    rating={supplier.rating}
                    reviews={supplier.reviews}
                    region={supplier.region}
                    guests={supplier.guests}
                    priceTier={supplier.priceTier}
                />
                {children}
            </Card.Body>
            {(showActionButtons && isNotCustomSupplier) && <div className="p-3">
                <Button
                    variant={'outline-primary'}
                    className={css.actionButton}
                    onClick={handleSupplierAction}
                    disabled={enquirySent}>
                        {buttonText}
                </Button>
            </div>}
        </Card>
    )
};

export default VWPDashboardSupplier;
